/**
 * Samtykke: the member's own confirmation of their membership — the board's numbers, who must be removed in Solidus,
 * the Digipost letters that ask the rest, and the signed paper form. Membership consent, phases 1 and 2.
 *
 * Since 2026-10-15 these are parts of «Medlemmer» (it was its own menu item, «Medlemsbekreftelse», a word that also
 * meant the mosque's PDF): the numbers, removals, letters and the seal under «Import og brev», the paper form on the
 * person's own page. «Samtykke» is the member's answer; «Medlemsbevis» is the mosque's document.
 *
 * - The numbers are over members still on the register. "Indirekte" is its
 *   own word: signed in with BankID or Vipps as themselves, never answered.
 *   Whether that counts for the grant is the lawyer's call, not this page's.
 * - Disputed members are listed for removal IN SOLIDUS. The app cannot remove
 *   anybody; Solidus is the register.
 * - Paper: for members who will never use BankID or Vipps, staff print a
 *   form with the exact words, the member signs, staff scan it and record
 *   it here. Its own permission (consent.record_paper): it creates evidence.
 * - Letters: the Solidus export is uploaded for the preview and again for
 *   the run, because the server keeps no fødselsnummer or address between
 *   the two. The send button carries the count the preview showed, and the
 *   server refuses a different one.
 * - Nothing here names an environment variable or a ticket: what is off is «slått på av Min Masjid».
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { download, errText, post, readUpload, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { isoToday, osloTime, plural } from '../lib/format';
import { Body as CardBody, Btn, Card, CopyField, DropZone, Field, LoadError, Loading, More, Msg, Tag, TwoStep } from '../components/ui';

const COUNT_NO: [string, string, 'green' | 'gold' | 'grey' | 'red'][] = [
  ['confirmed', 'Bekreftet', 'green'],
  ['indirect', 'Indirekte (logget inn, ikke svart)', 'gold'],
  ['unconfirmed', 'Ikke bekreftet', 'grey'],
  ['disputed', 'Sier de ikke har meldt seg inn', 'red'],
];
const SKIPPED_NO: Record<string, string> = {
  answered: 'har allerede svart',
  under15: 'er under 15 år (forelderen får brevet)',
  ageUnknown: 'mangler fødselsår — må følges opp av kontoret',
  recentlySent: 'fikk brev de siste 60 dagene',
};
const CHANNEL_NO: Record<string, string> = { DIGIPOST: 'Digipost', PRINT: 'papir', PENDING: 'venter', unknown: 'allerede levert' };

/** The board's numbers, the removals for Solidus, and the seal — one load of GET /membership-consent (members.import). */
export function ConsentOverview() {
  const { data, error, reload } = useLoad<Body>('/membership-consent');
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  if (!data.enabled) {
    return <Card title="Samtykke" hint="Medlemmenes egen bekreftelse på at de har meldt seg inn.">
      <CardBody><p className="msg" role="status" style={{ marginTop: 0 }}>Ikke slått på for denne moskeen. Slås på av Masjid når styret har godkjent teksten og personvernerklæringen.</p></CardBody>
    </Card>;
  }
  const c = data.counts || {};
  const removals: Body[] = data.removeInSolidus || [];
  return (
    <>
      <Card title="Samtykke" hint="Hvem av medlemmene har selv bekreftet at de er meldt inn."
        more={<p>Moskeen må kunne vise at hvert medlem har meldt seg inn selv, eller er meldt inn av en forelder før fylte 15 år. Medlemmer svarer i appen med BankID eller Vipps og får en forseglet kvittering. Å ikke svare er aldri et ja. «Indirekte» betyr at medlemmet har logget inn med BankID eller Vipps og blitt funnet som seg selv, men ikke har svart. Om det holder som bevis for tilskudd, avgjør advokaten.</p>}>
        <table className="t">
          <thead><tr><th scope="col">Svar</th><th scope="col">Antall</th><th scope="col">Andel</th></tr></thead>
          <tbody>{COUNT_NO.map(([k, label, tone]) => (
            <tr key={k}><td><Tag tone={tone}>{label}</Tag></td><td>{c[k] || 0}</td>
              <td>{c.total ? Math.round(100 * (c[k] || 0) / c.total) + ' %' : '—'}</td></tr>))}
            <tr><td><b>Medlemmer i registeret</b></td><td><b>{c.total || 0}</b></td><td /></tr>
          </tbody>
        </table>
        <CardBody>
          <span className="field note" style={{ display: 'block' }}>{data.confirmedBy && <>Av dem som har bekreftet, svarte {data.confirmedBy.app} i appen og {data.confirmedBy.paper} på papir. </>}Tekstversjon: {data.textVersion}.</span>
          {/* The seal: rarely needed, and needed for years — folded, never gone. */}
          <More label="Bevis og forsegling">
            <p style={{ marginTop: 0 }}>Hvert svar lagres som en kvittering som ikke kan endres eller slettes før lagringstiden er ute: i <b>{data.evidence?.store === 's3' ? 'AWS S3 (Stockholm), låst med Object Lock' : 'lokal disk (utvikling)'}</b> i {data.evidence?.retainYears} år.
              {data.evidence?.devSeal && <> <Tag tone="red">Utviklingsnøkkel</Tag> Kvitteringer forseglet med denne nøkkelen er ikke bevis.</>}</p>
            <CopyField label={`Offentlig nøkkel som kontrollerer forseglingen (${data.evidence?.sealKeyId})`} value={data.evidence?.sealPublicKey || ''} />
          </More>
        </CardBody>
      </Card>

      <Card title={'Fjernes i medlemsregisteret · ' + removals.length} hint="Disse har svart at de ikke har meldt seg inn — fjern dem i medlemsregisteret; neste import tar det med.">
        {removals.length ? <table className="t">
          <thead><tr><th scope="col">Medlemsnr.</th><th scope="col">Navn</th><th scope="col">Svarte</th></tr></thead>
          <tbody>{removals.map((r: Body) => (
            <tr key={r.memberId}><td>{r.memberNo}</td><td><a href={`#/medlemmer/person/${encodeURIComponent(r.memberId)}`}>{r.name}</a></td><td>{osloTime(r.at)}</td></tr>))}</tbody>
        </table> : <CardBody>Ingen å fjerne.</CardBody>}
      </Card>
    </>
  );
}

const CHECK_NO: [string, string][] = [
  ['desk_with_id', 'Signert her i moskeen — legitimasjon er vist'],
  ['returned_by_post', 'Mottatt i posten — hvem som signerte, er ikke kontrollert'],
  ['historical_form', 'Gammelt innmeldingsskjema fra arkivet'],
];
const FORM_TYPES: Record<string, string> = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png' };

function readScan(file: File): Promise<{ formBase64: string; formType: string }> {
  const type = FORM_TYPES[(file.name.split('.').pop() || '').toLowerCase()];
  if (!type) return Promise.reject(new Error('Bruk PDF, JPG eller PNG.'));
  if (file.size > 10 * 1024 * 1024) return Promise.reject(new Error('Filen er større enn 10 MB. Skann med lavere oppløsning.'));
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(new Error('Filen kunne ikke leses.'));
    fr.onload = () => resolve({ formBase64: String(fr.result).replace(/^data:[^,]*,/, ''), formType: type });
    fr.readAsDataURL(file);
  });
}

/**
 * The help desk, on the person's page: the member's answer so far, the form to print, and the signed scan to record.
 * Looked up by the person's member number when the section is opened (consent.record_paper; the lookup is logged).
 */
export function PaperConsent({ memberNo }: { memberNo: string }) {
  const [m, setM] = useState<Body | null>(null);
  const [off, setOff] = useState(false);
  const [guardianNo, setGuardianNo] = useState('');
  const [answer, setAnswer] = useState('confirmed');
  const [check, setCheck] = useState('desk_with_id');
  const [signedOn, setSignedOn] = useState(isoToday());
  const [scan, setScan] = useState<{ name: string; formBase64: string; formType: string } | null>(null);
  const [msg, say] = useMsg();
  const input = useRef<HTMLInputElement>(null);
  const clear = () => { setM(null); setGuardianNo(''); setAnswer('confirmed'); setCheck('desk_with_id'); setSignedOn(isoToday()); setScan(null); if (input.current) input.current.value = ''; };

  const find = useCallback(async () => {
    say(''); clear();
    const r = await send(`/membership-consent/paper/member?no=${encodeURIComponent(memberNo)}`);
    if (r.status === 404 && r.body && r.body.message === 'Ikke slått på.') { setOff(true); return; }
    if (!r.ok) return say(errText(r.body), 'err');
    setM(r.body);
    if (!r.body.mayAnswerSelf && r.body.guardians.length) setGuardianNo(r.body.guardians[0].memberNo);
  }, [memberNo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { void find(); }, [find]);
  if (off) return <p className="note">Samtykke er ikke slått på for denne moskeen. Slås på av Masjid.</p>;
  const signerOk = m && (guardianNo ? true : m.mayAnswerSelf);

  return (
    <div>
        <p className="note" style={{ marginTop: 0 }}>For medlemmer uten BankID eller Vipps: skriv ut skjemaet, la medlemmet (eller en forelder for barn under 15) krysse av og skrive under, skann det og registrer svaret. Skjemaet lagres som bevis og kan ikke endres.</p>
        <Msg text={msg.text} tone={msg.tone} />

        {m && <>
          <p>{m.latest ? <>Svar: <Tag tone={m.latest.answer === 'confirmed' ? 'green' : 'red'}>{m.latest.answer === 'confirmed' ? 'bekreftet' : 'bestrider'}</Tag> ({m.latest.method === 'staff_paper' ? 'papir' : 'appen'}, {osloTime(m.latest.at)}). Et nytt svar erstatter det.</>
            : 'Har ikke svart.'}</p>
          {m.guardians.length > 0 && <Field label="Hvem signerer?" className="field">
            {(id) => <select id={id} value={guardianNo} onChange={(e) => setGuardianNo(e.target.value)}>
              {m.mayAnswerSelf && <option value="">Medlemmet selv</option>}
              {m.guardians.map((g: Body) => <option key={g.memberNo} value={g.memberNo}>{g.name} (forelder, {g.memberNo})</option>)}
            </select>}
          </Field>}
          {!signerOk && <p className="msg err">{m.message || 'Ingen kan signere for dette medlemmet ennå.'} {m.reason === 'guardian_must_answer' ? 'Legg inn en forelder under «Husstand» på denne siden først.' : ''}</p>}

          {signerOk && <>
            <div className="row"><Btn ghost onClick={async () => {
              const q = `no=${encodeURIComponent(m.memberNo)}${guardianNo ? `&guardianNo=${encodeURIComponent(guardianNo)}` : ''}`;
              const r = await download(`/v1/admin/membership-consent/paper/form?${q}`, `medlemskap-skjema-${m.memberNo}.pdf`);
              if (!r.ok) say(r.message || 'Skjemaet kunne ikke lastes ned.', 'err');
            }}>Skriv ut skjema</Btn></div>

            <h4 style={{ marginTop: 18 }}>Registrer det signerte skjemaet</h4>
            <fieldset className="field" style={{ border: 0, padding: 0 }}>
              <legend>Hva er krysset av?</legend>
              <label style={{ display: 'block' }}><input type="radio" name="paper-answer" checked={answer === 'confirmed'} onChange={() => setAnswer('confirmed')} /> Bekrefter medlemskapet</label>
              <label style={{ display: 'block' }}><input type="radio" name="paper-answer" checked={answer === 'disputed'} disabled={check === 'historical_form'}
                onChange={() => setAnswer('disputed')} /> Har ikke meldt {guardianNo ? 'barnet' : 'seg'} inn</label>
            </fieldset>
            <fieldset className="field" style={{ border: 0, padding: 0 }}>
              <legend>Hvordan kom skjemaet inn?</legend>
              {CHECK_NO.map(([k, label]) => <label key={k} style={{ display: 'block' }}>
                <input type="radio" name="paper-check" checked={check === k} onChange={() => { setCheck(k); if (k === 'historical_form') setAnswer('confirmed'); }} /> {label}</label>)}
            </fieldset>
            <Field label="Datoen på skjemaet" className="field half">
              {(id) => <input id={id} type="date" max={isoToday()} value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />}
            </Field>
            <DropZone title={scan ? `Valgt: ${scan.name}` : 'Slipp det skannede skjemaet her, eller klikk for å velge fil'} accept=".pdf,.jpg,.jpeg,.png"
              inputRef={input} onFile={async (f) => { try { setScan({ name: f.name, ...(await readScan(f)) }); say(''); } catch (e) { say((e as Error).message, 'err'); } }}>
              <span className="sub">PDF, JPG eller PNG, høyst 10 MB. Hele skjemaet med signaturen må være lesbart.</span>
            </DropZone>
            <div className="row" style={{ marginTop: 14 }}>
              <TwoStep ghost={false} disabled={!scan || !signedOn} label="Registrer svaret" confirmLabel="Bekreft: lagre som bevis" onConfirm={async () => {
                const r = await post('/membership-consent/paper', { memberNo: m.memberNo, guardianNo: guardianNo || undefined, answer, check, signedOn,
                                                                    formBase64: scan!.formBase64, formType: scan!.formType });
                if (!r.ok) return say(errText(r.body), 'err');
                await find(); say(`Registrert for ${m.name}. Medlemmet ser kvitteringen i appen.`, 'ok');
              }} />
            </div>
          </>}
        </>}
    </div>
  );
}

export function Letters() {
  const { data, error, reload } = useLoad<Body>('/membership-consent/letters');
  const [pending, setPending] = useState<Body | null>(null);
  const [preview, setPreview] = useState<Body | null>(null);
  const [msg, say] = useMsg();
  const input = useRef<HTMLInputElement>(null);
  const reset = useCallback(() => { setPending(null); setPreview(null); if (input.current) input.current.value = ''; }, []);

  // While a run is going, follow it.
  const running = Boolean(data && ((data.run && data.run.running) || (data.reach && data.reach.running)));
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => { void reload(); }, 1500);
    return () => clearInterval(t);
  }, [running, reload]);

  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const title = 'Brev om samtykke';
  const hint = 'Medlemmer som ikke har svart, får et brev i Digipost som ber dem svare i appen.';
  const more = <p>Har medlemmet ikke Digipost, og papir er slått på, sendes brevet på papir med Posten. Brevet ber medlemmet svare i appen med BankID eller Vipps.</p>;
  if (!data.enabled) {
    return <Card title={title} hint={hint}><CardBody>
      <p className="msg" role="status" style={{ marginTop: 0 }}>Ikke satt opp for denne moskeen. Det krever en Digipost-avtale, og slås på av Masjid.</p>
    </CardBody></Card>;
  }

  async function onFile(file: File) {
    say(''); setPreview(null);
    if (!/\.xlsx$/i.test(file.name)) return say('Bruk registereksporten som .xlsx, med kolonnen for fødselsnummer.', 'err');
    let body: Body;
    try { body = await readUpload(file); } catch (e) { reset(); return say((e as Error).message, 'err'); }
    const r = await post('/membership-consent/letters/preview', { xlsxBase64: body.xlsxBase64 });
    if (!r.ok) { reset(); return say(errText(r.body), 'err'); }
    setPending({ xlsxBase64: body.xlsxBase64, filename: file.name }); setPreview(r.body);
  }

  async function start() {
    if (!pending || !preview) return;
    const r = await post('/membership-consent/letters', { xlsxBase64: pending.xlsxBase64, expected: preview.ready });
    if (!r.ok) { if (r.body.code === 'count_changed') setPreview(null); return say(errText(r.body), 'err'); }
    say(`Utsendingen har startet: ${plural(r.body.total, 'brev', 'brev')}.`, 'ok');
    reset(); void reload();
  }

  async function checkReach() {
    if (!pending) return;
    const r = await post('/membership-consent/letters/reach', { xlsxBase64: pending.xlsxBase64 });
    if (!r.ok) return say(errText(r.body), 'err');
    say('Sjekker hos Digipost … Ingen brev sendes.', 'ok'); void reload();
  }

  const run = data.run;
  const reach = data.reach;
  const t = data.totals || {};
  return (
    <Card title={title} hint={hint} more={more}>
      <CardBody>
        {data.digipost?.kind === 'fake' && <p className="msg err" role="status" style={{ marginTop: 0 }}>Utviklingsmodus: ingen brev forlater denne maskinen. Digipost er ikke koblet til.</p>}
        <p style={{ marginTop: 0 }}>Sendt til nå: <b>{t.sent || 0}</b>
          {Object.entries(t.byChannel || {}).map(([k, v]) => <span key={k}> · {CHANNEL_NO[k] || k}: {String(v)}</span>)}
          {t.failed ? <> · <span className="sub">feilet: {t.failed}</span></> : null}
          . Papir: {data.digipost?.printFallback ? 'på' : 'av'}. Åpning krever: {data.digipost?.authenticationLevel === 'IDPORTEN_4' ? 'BankID' : data.digipost?.authenticationLevel === 'TWO_FACTOR' ? 'innlogging med to faktorer' : data.digipost?.authenticationLevel || 'Digipost-innlogging'}.</p>

        {run && <div role="status" aria-live="polite" className="field note" style={{ display: 'block', marginBottom: 14 }}>
          {run.running ? <b>Sender … {run.done} av {run.total}</b> : <b>Siste utsending ({osloTime(run.finishedAt)}):</b>}
          {' '}{run.sent} sendt{run.failed ? `, ${run.failed} feilet` : ''}
          {Object.entries(run.byChannel || {}).filter(([, v]) => v).map(([k, v]) => ` · ${CHANNEL_NO[k] || k}: ${v}`).join('')}
          {run.stoppedMessage && <p className="msg err" style={{ marginBottom: 0 }}>{run.stoppedMessage}</p>}
        </div>}

        {reach && <div role="status" aria-live="polite" className="field note" style={{ display: 'block', marginBottom: 14 }}>
          {reach.running ? <b>Sjekker hos Digipost … {reach.done} av {reach.total}</b> : <b>Digipost-sjekk ({osloTime(reach.finishedAt)}):</b>}
          {' '}{reach.digipost} har Digipost · {reach.noDigipost} har ikke Digipost
          {reach.noDigipost ? ` (${reach.noDigipostWithAddress} av dem har adresse i filen, ${reach.noDigipost - reach.noDigipostWithAddress} kan ikke nås med brev)` : ''}
          {reach.unknown ? ` · ${reach.unknown} ukjent hos Digipost` : ''}{reach.failed ? ` · ${reach.failed} feilet` : ''}
          {reach.stoppedMessage && <p className="msg err" style={{ marginBottom: 0 }}>{reach.stoppedMessage}</p>}
        </div>}

        {!running && <DropZone title="Slipp registereksporten (.xlsx) her, eller klikk for å velge fil" accept=".xlsx" onFile={onFile} inputRef={input}>
          <span className="sub">Samme fil som til medlemsimporten, med fødselsnummer og adresse. Fødselsnummer og adresse brukes bare til denne utsendingen og lagres ikke.</span>
        </DropZone>}
        <Msg text={msg.text} tone={msg.tone} />

        {preview && <>
          <h3 style={{ marginTop: 22 }}>Forhåndsvisning — {pending?.filename}</h3>
          <ul>
            <li><b>{plural(preview.ready, 'brev', 'brev')} klare</b>{preview.children ? `, som også nevner ${plural(preview.children, 'barn', 'barn')} under 15 år` : ''}.
              {' '}{preview.readyWithAddress} har adresse for papirbrev{data.digipost?.printFallback ? '' : ' (papir er av — de uten Digipost får ikke brevet)'}.</li>
            {Object.entries(preview.skipped || {}).filter(([, v]) => v).map(([k, v]) => <li key={k}>{String(v)} {SKIPPED_NO[k] || k}.</li>)}
            {preview.problemCount ? <li><b>{preview.problemCount} får ikke brev</b> — se listen under.</li> : null}
          </ul>
          {preview.sample && <details>
            <summary>Slik ser brevet ut (medlemsnr. {preview.sample.memberNo})</summary>
            <div className="field note" style={{ display: 'block', whiteSpace: 'pre-wrap', border: '1px solid var(--line, #ddd)', padding: 14, marginTop: 8 }}>
              {preview.sample.lines.join('\n')}
            </div>
          </details>}
          {preview.problems?.length ? <table className="t" style={{ marginTop: 14 }}>
            <thead><tr><th scope="col">Medlemsnr.</th><th scope="col">Navn</th><th scope="col">Hvorfor ikke</th></tr></thead>
            <tbody>{preview.problems.map((p: Body) => <tr key={p.memberNo}><td>{p.memberNo}</td><td>{p.name || '—'}</td><td>{p.message}</td></tr>)}</tbody>
          </table> : null}
          <div className="row" style={{ marginTop: 14 }}>
            {preview.ready > 0 && <><Btn ghost onClick={checkReach}>Sjekk hvor mange som har Digipost</Btn>{' '}</>}
            {preview.ready > 0 && <TwoStep ghost={false} label={`Send ${plural(preview.ready, 'brev', 'brev')}`}
              confirmLabel={`Bekreft: send ${plural(preview.ready, 'brev', 'brev')} nå`} onConfirm={start} />}{' '}
            <Btn ghost onClick={() => { reset(); say(''); }}>Avbryt</Btn>
          </div>
        </>}
      </CardBody>
    </Card>
  );
}
